import {
  reduce,
  differenceWith,
  intersectionWith,
  includes,
  replace,
  camelCase,
} from 'lodash';

const createModelPermission = (scope, resource, inputedValue) => {
  if (!inputedValue) return null;

  return { scope, resource, value: inputedValue };
};

const createFieldsPermissions = (scope, resource, fields) => {
  return reduce(
    fields,
    (res, value, field) => {
      const modelsField = `${resource}.${field}`;

      if (value) {
        return [...res, { scope, resource: modelsField, value }];
      }

      return res;
    },
    [],
  );
};

const prepareResourcePermissions = (scope, models) => {
  return reduce(
    models,
    (result, { value, columns }, modelName) =>
      [
        ...result,
        ...createFieldsPermissions(scope, modelName, columns),
        createModelPermission(scope, modelName, value),
      ].filter(perm => perm !== null),
    [],
  );
};

const preparePermissions = models => {
  return reduce(
    models,
    (result, productModels, scope) => [
      ...result,
      ...prepareResourcePermissions(scope, productModels),
    ],
    [],
  );
};

const isSamePermission = (n, m) => {
  return (
    m.resource === n.resource && m.value === n.value && m.scope === n.scope
  );
};

const calculateDiffenrence = (userInput, assigned, actionType) => {
  // new permissions to create and add:
  const permissions = differenceWith(userInput, assigned, isSamePermission);
  // existing permissions to remove from role:
  const dropIds = differenceWith(assigned, userInput, isSamePermission).map(
    ({ id }) => id,
  );
  // existing permissions to save:
  const addIds = intersectionWith(assigned, userInput, isSamePermission).map(
    ({ id }) => id,
  );

  return {
    permissions,
    ...(actionType === 'edit' && { drop_permissions_ids: dropIds }),
    ...(actionType !== 'edit' && { add_permissions_ids: addIds }),
  };
};

const populateWithAppsPermissions = (models = {}, apps) => {
  if (!apps) return models;

  return reduce(
    apps,
    (res, value, key) => ({
      ...res,
      [key]: { ...res[key], [key]: value[key] },
    }),
    models,
  );
};

export const prepareRoleForSaving = (values, initialValues, actionType) => {
  const { models, apps, assigned_roles: assigned = [], role } = values;
  const { assigned_permissions: assignedPerms, assigned_roles: assignedRoles } =
    initialValues || {};
  const newAssignedRoles = assigned.map(option => option.value || option); // @TODO only new
  const preparedResources = populateWithAppsPermissions(models, apps);

  const model = {
    role: {
      ...role,
      name: role.name && role.name.trim(),
    },
    add_roles_ids: newAssignedRoles,
    permissions: preparePermissions(preparedResources),
  };

  if (assignedPerms || assignedRoles) {
    const normalizedAssignedRoles = (assignedRoles || []).map(
      option => option.value || option,
    );
    const dropRolesId = differenceWith(
      normalizedAssignedRoles,
      newAssignedRoles,
      (n, m) => n === m,
    );

    return {
      ...model,
      ...(actionType === 'edit' && { drop_roles_ids: dropRolesId }),
      ...calculateDiffenrence(model.permissions, assignedPerms, actionType),
    };
  }

  return model;
};

export const prepareSearchParams = params => {
  const { page, limit, filters, sort, ...restData } = params;
  const preparedParams = {
    page_size: limit,
    ...filters,
    ...restData,
  };

  if (page > 1) preparedParams.page_number = page - 1;

  if (sort) {
    preparedParams.order_by_asc = includes(sort, ',asc');
    let sortAttribute = replace(sort, 'filter_by_', '');

    sortAttribute = replace(sortAttribute, ',asc', '');
    sortAttribute = replace(sortAttribute, ',desc', '');
    preparedParams.sort_attribute = camelCase(sortAttribute);
  }

  return preparedParams;
};
